

import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Divider,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

/**
 * PermissionSetting
 * Manage roles and their permissions for the Concierge app.
 *
 * Props:
 * - initialRoles: Array<{ id: string, name: string, permissions: Record<string, boolean> }>
 * - onSave: (roles) => Promise<void> | void
 */
export default function PermissionSetting({ initialRoles = [], onSave }) {
  const defaultPerms = useMemo(
    () => ({
      manage_bot: true,
      manage_responses: true,
      manage_automations: true,
      view_transcripts: true,
      assign_conversations: true,
      close_conversations: true,
      edit_settings: true,
    }),
    []
  );

  const canned = useMemo(
    () => ([
      { id: "admin", name: "Admin", permissions: { ...defaultPerms } },
      {
        id: "manager",
        name: "Manager",
        permissions: {
          ...defaultPerms,
          edit_settings: true,
        },
      },
      {
        id: "staff",
        name: "Staff",
        permissions: {
          manage_bot: false,
          manage_responses: true,
          manage_automations: false,
          view_transcripts: true,
          assign_conversations: true,
          close_conversations: true,
          edit_settings: false,
        },
      },
      {
        id: "viewer",
        name: "Viewer",
        permissions: {
          manage_bot: false,
          manage_responses: false,
          manage_automations: false,
          view_transcripts: true,
          assign_conversations: false,
          close_conversations: false,
          edit_settings: false,
        },
      },
    ]),
    [defaultPerms]
  );

  const [roles, setRoles] = useState(initialRoles.length ? initialRoles : canned);
  const [saving, setSaving] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  const permColumns = [
    { id: "manage_bot", label: "Bot Messages" },
    { id: "manage_responses", label: "Fast Responses" },
    { id: "manage_automations", label: "Automations" },
    { id: "view_transcripts", label: "View Transcripts" },
    { id: "assign_conversations", label: "Assign Conversations" },
    { id: "close_conversations", label: "Close Conversations" },
    { id: "edit_settings", label: "Edit Settings" },
  ];

  const addRole = () => {
    const name = newRoleName.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/\s+/g, "_");
    setRoles((prev) => [
      ...prev,
      { id, name, permissions: { ...defaultPerms, edit_settings: false } },
    ]);
    setNewRoleName("");
  };

  const removeRole = (id) => {
    setRoles((prev) => prev.filter((r) => r.id !== id));
  };

  const toggle = (roleId, permId) => {
    setRoles((prev) =>
      prev.map((r) =>
        r.id === roleId
          ? {
              ...r,
              permissions: { ...r.permissions, [permId]: !r.permissions?.[permId] },
            }
          : r
      )
    );
  };

  const renameRole = (roleId, name) => {
    setRoles((prev) => prev.map((r) => (r.id === roleId ? { ...r, name } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = roles.map((r) => ({
        id: r.id,
        name: r.name.trim() || "Untitled",
        permissions: permColumns.reduce(
          (acc, p) => ({ ...acc, [p.id]: !!r.permissions?.[p.id] }),
          {}
        ),
      }));
      await onSave?.(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">👥 Roles &amp; Permissions</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={addRole}
            disabled={!newRoleName.trim()}
          >
            Add role
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Create a new role
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Role name (e.g., Reception)"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <Chip label="Add role" onClick={addRole} />
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 260 }}>Role</TableCell>
              {permColumns.map((p) => (
                <TableCell key={p.id} align="center" sx={{ whiteSpace: "nowrap" }}>
                  {p.label}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ width: 56 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <TextField
                    size="small"
                    value={r.name}
                    onChange={(e) => renameRole(r.id, e.target.value)}
                  />
                </TableCell>
                {permColumns.map((p) => (
                  <TableCell key={`${r.id}_${p.id}`} align="center">
                    <Checkbox
                      checked={!!r.permissions?.[p.id]}
                      onChange={() => toggle(r.id, p.id)}
                      inputProps={{ "aria-label": `${r.name}-${p.label}` }}
                    />
                  </TableCell>
                ))}
                <TableCell align="center">
                  <IconButton onClick={() => removeRole(r.id)} aria-label="Delete role">
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={permColumns.length + 2}>
                  <Typography variant="body2" color="text.secondary">
                    No roles yet. Create one above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Tip: use roles to keep access tidy. Only **Admin** should keep “Edit Settings”.
      </Typography>
    </Box>
  );
}